import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Input,
  CardHeader,
  Button,
  ButtonGroup,
} from "reactstrap";
import DataTable from "react-data-table-component";
import Select from "react-select";
import data from "../data/data";
import Flatpickr from "react-flatpickr";
import CustomTable from "../../../../Components/Common/CustomTable/CustomTable";
import MonthlyReportServices from "../../../../services/COOPServices/MonthlyReportServices/MonthlyReportServices";
import DeleteModal from "../../../../Components/Common/DeleteModal/DeleteModal";
import toast from "react-hot-toast";

const statusColors = {
  Draft: "bg-danger",
  Submitted: "bg-success",
  Acknowledged: "bg-primary",
  Approved: "bg-success",
};

const ReportList = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [reportList, setReportList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [dateRange, setDateRange] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);

  const [filter, setFilter] = useState({
    submissionNo: "",
    startDate: "",
    endDate: "",
    status: "",
    cooperativeId: "",
    coopEmail: "",
    pageNumber: 1,
    pageSize: 100,
  });

  useEffect(() => {
    fetchReportList();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [reportList, searchQuery, selectedStatus, dateRange]);

  const fetchReportList = async () => {
    setLoading(true);
    try {
      const response = await MonthlyReportServices.getList({ ...filter });
      setReportList(response.data);
    } catch (error) {
      console.log("🚀 ~ fetchReportList ~ error:", error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...reportList];

    // Apply search filter
    if (searchQuery !== "") {
      filtered = filtered.filter((item) =>
        item.submissionNo?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply status filter
    if (selectedStatus) {
      filtered = filtered.filter(
        (item) => item.status === selectedStatus.value
      );
    }

    // Apply date range filter
    if (dateRange.length === 2) {
      const [startDate, endDate] = dateRange;
      filtered = filtered.filter((item) => {
        const itemDate = new Date(item.submissionDate);
        return itemDate >= startDate && itemDate <= endDate;
      });
    }

    setFilteredData(filtered);
  };

  const handleDeleteModal = () => {
    setIsDeleteOpen(!isDeleteOpen);
  };

  const handleNavigationForm = () => {
    navigate("/create-report");
  };

  const handleEdit = (id) => {
    console.log("🚀 ~ handleEdit ~ d:", id);
    navigate(`/draft-report/${id}`);
  };

  const handleDelete = async () => {
    try {
      await MonthlyReportServices.delete(selectedReport);
      fetchReportList();
      toast.success("Report Deleted Successfully!");
    } catch (error) {
      toast.error("Something went wrong!");
      console.log("🚀 ~ handleDelete ~ error:", error);
    }
  };

  const columns = [
    {
      name: "S.N",
      selector: (row) => row.sn,
      sortable: true,
      width: "80px",
    },
    {
      name: "Submission No.",
      selector: (row) => row.submissionNo ?? "Report Not Submitted",
      sortable: true,
    },
    {
      name: "Report Name",
      selector: (row) => row.monthlyReportName ?? "NA",
      sortable: true,
    },
    {
      name: "Report Name",
      selector: (row) => row.reportType ?? "NA",
      sortable: true,
    },
    // {
    //   name: "Created Date",
    //   selector: (row) =>
    //     new Date(row.createDate).toLocaleDateString() ?? "No data available",
    //   sortable: true,
    // },
    {
      name: "Submitted Date",
      selector: (row) =>
        new Date(row.submissionDate).toLocaleDateString() ??
        "No data available",

      sortable: true,
    },
    // {
    //   name: "Amount",
    //   selector: (row) => row.amount ?? "No data available",
    //   sortable: true,
    // },
    {
      name: "Status",
      selector: (row) => (
        <span
          className={`badge ${
            statusColors[row.status] || ""
          }-subtle text-uppercase text-dark`}
        >
          {row.status === "Draft"
            ? "Draft"
            : row.status === "Submitted"
            ? "Submitted"
            : row.status === "Acknowledged"
            ? "Acknowledged"
            : row.status === "Approved"
            ? "Approved"
            : "No data available"}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Action",
      cell: (row) => row.action,
    },
  ];

  const dataRows = filteredData?.map((item, index) => ({
    ...item,
    sn: index + 1,
    submissionNo: (
      <Link to={`/report/detail/${item.monthlyReportId}`}>
        {item.submissionNo}
      </Link>
    ),
    action: (
      <ButtonGroup size="sm">
        <div className="d-flex gap-1">
          {!(
            item.status === "Submitted" ||
            item.status === "Approved" ||
            item.status === "Acknowledged"
          ) && (
            <>
              <Link
                to={`/draft-report/${item.monthlyReportId}`}
                state={{ status: item.status }}
              >
                <Button
                  color="rounded-circle btn btn-soft-warning"
                  className="rounded-circle btn-sm gap-1"
                >
                  <i className="ri-edit-line"></i>
                </Button>
              </Link>

              <Button
                className="rounded-circle btn btn-soft-danger btn-sm gap-1"
                color="danger"
                onClick={() => {
                  handleDeleteModal();
                  setSelectedReport(item.monthlyReportId);
                }}
                title="Delete"
              >
                <i className="ri-delete-bin-2-line"></i>
              </Button>
            </>
          )}
        </div>
      </ButtonGroup>
    ),
  }));

  return (
    <Container fluid>
      <div className="page-content">
        <Row>
          <Col xs={6} className="mb-0">
            <h5>
              <span className="px-1">Reports</span>
            </h5>
          </Col>
          <Col xs={6} className="text-end">
            <Button
              color="success"
              onClick={handleNavigationForm}
              className="float-end"
            >
              <i className="ri-add-line align-bottom me-1"></i>
              Create New Report
            </Button>
          </Col>
        </Row>
        <Row className="gap-0 mb-2">
          <Col xs={3}>
            <div className="search-box">
              <input
                type="text"
                className="form-control search"
                placeholder="Search by submission no..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <i className="ri-search-line search-icon"></i>
            </div>
          </Col>
          <Col xs={3}>
            <div className="">
              <Flatpickr
                className="form-control"
                id="datepicker-publish-input"
                placeholder="Select a range of date"
                options={{
                  altInput: true,
                  altFormat: "F j, Y",
                  mode: "range",
                  dateFormat: "d.m.y",
                }}
                value={dateRange}
                onChange={(dates) => setDateRange(dates)}
              />
            </div>
          </Col>
          <Col xs={3}>
            <div className="search-box">
              <Select
                isClearable={true}
                placeholder="Select by status"
                options={[
                  { value: "Draft", label: "Draft" },
                  { value: "Submitted", label: "Submitted" },
                  { value: "Acknowledged", label: "Acknowledged" },
                  { value: "Approved", label: "Approved" },
                ]}
                value={selectedStatus}
                onChange={setSelectedStatus}
              />
            </div>
          </Col>
        </Row>

        <CustomTable columns={columns} data={dataRows} loading={loading} />
        <DeleteModal
          deleteTerm={"report"}
          handleDelete={handleDelete}
          isOpen={isDeleteOpen}
          toggle={handleDeleteModal}
        />
      </div>
    </Container>
  );
};

export default ReportList;
